import React, { useState, useEffect, useCallback } from 'react';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import api from '../../services/api';
import AssignLockerModal from '../../components/Store/AssignLockerModal';
import './PickupOrders.css';

interface OrderItem {
  id: number;
  name: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface LockerAssignment {
  id: number;
  locker_number: number;
  locker_name: string;
  passcode: string;
  assigned_at: string;
  status: string;
}

interface PickupOrder {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  pickup_method: 'in_store' | 'locker';
  status: string;
  items: OrderItem[];
  items_summary: string;
  total: number;
  created_at: string;
  paid_at: string | null;
  locker_assignment: LockerAssignment | null;
  needs_locker: boolean;
  // Phase B fields (BridgeClubOrder only — surfaced for the camera
  // icon + snapshot display). May be absent on StoreClubOrder rows.
  completion_method?: 'manual_staff_button' | 'auto_via_webhook' | 'manual_admin' | '';
  pickup_snapshot_url?: string;
  picked_up_at?: string | null;
}

interface Locker {
  id: number;
  number: number;
  name: string;
  capacity_bags: number;
  is_occupied: boolean;
}

interface Stats {
  total: number;
  needs_filling: number;
  locker_orders: number;
  instore_orders: number;
}

const PickupOrders: React.FC = () => {
  const { user } = useStoreAuth();
  const [orders, setOrders] = useState<PickupOrder[]>([]);
  const [lockers, setLockers] = useState<Locker[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'needs_filling' | 'locker' | 'in_store'>('all');

  // Modal state — actual assign UI lives in <AssignLockerModal>.
  // We just track which order is being assigned + a transient success
  // toast so the page can show "Locker N assigned, customer notified".
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PickupOrder | null>(null);

  // Phase A — Manual Override "Picked Up" safety net.
  // Pre-fix flow used window.confirm which was easy to click through.
  // Now: dedicated confirm modal + post-confirm undo toast with a
  // 60-second window. The underlying button is relabeled to
  // "Manual Override: Mark Picked Up" so the auto-via-webhook path
  // (Phase B/C, commit #3) becomes the primary expectation.
  const [pickupConfirm, setPickupConfirm] = useState<PickupOrder | null>(null);
  const [pickupSubmitting, setPickupSubmitting] = useState(false);
  const [undoToast, setUndoToast] = useState<{
    orderId: number;
    orderNumber: string;
    expiresAt: number;  // epoch ms
  } | null>(null);
  const [undoSubmitting, setUndoSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState<{
    kind: 'success' | 'error';
    text: string;
  } | null>(null);
  // Tick state so the undo countdown re-renders each second.
  const [, setNowTick] = useState(0);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get('/store/pickup-orders/');
      setOrders(response.data.orders);
      setStats(response.data.stats);
      setError('');
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLockers = useCallback(async () => {
    try {
      const response = await api.get('/store/lockers/dashboard/');
      setLockers(response.data.lockers);
    } catch (err: any) {
      console.error('Error fetching lockers:', err);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchLockers();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchOrders();
      fetchLockers();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchOrders, fetchLockers]);

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'needs_filling') return order.status === 'paid';
    if (filter === 'locker') return order.pickup_method === 'locker';
    if (filter === 'in_store') return order.pickup_method === 'in_store';
    return true;
  });

  const handleAssignLocker = (order: PickupOrder) => {
    setSelectedOrder(order);
    setShowAssignModal(true);
  };

  // Modal pushes the entire flow now. We just refresh and close on success.
  const handleAssignSuccess = () => {
    fetchOrders();
    fetchLockers();
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedOrder(null);
  };

  const handleMarkReady = async (order: PickupOrder) => {
    try {
      await api.post(`/store/pickup-orders/${order.id}/mark-ready/`);
      fetchOrders();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to mark order as ready');
    }
  };

  // Phase A — open the dedicated confirm modal instead of the
  // browser-native confirm dialog the user accidentally clicked
  // through tonight.
  const handleCompletePickup = (order: PickupOrder) => {
    setPickupConfirm(order);
  };

  const confirmManualPickup = async () => {
    if (!pickupConfirm) return;
    const order = pickupConfirm;
    setPickupSubmitting(true);
    try {
      await api.post(`/store/pickup-orders/${order.id}/complete/`);
      setPickupConfirm(null);
      setPickupSubmitting(false);
      // Set the 60-second undo toast. Backend's
      // PICKUP_UNDO_WINDOW_SECONDS default is 60.
      const UNDO_WINDOW_MS = 60 * 1000;
      setUndoToast({
        orderId: order.id,
        orderNumber: order.order_number,
        expiresAt: Date.now() + UNDO_WINDOW_MS,
      });
      fetchOrders();
      fetchLockers();
    } catch (err: any) {
      setPickupSubmitting(false);
      setPickupConfirm(null);
      setToastMessage({
        kind: 'error',
        text: err.response?.data?.error || 'Failed to complete pickup',
      });
      setTimeout(() => setToastMessage(null), 4000);
    }
  };

  // Phase A — undo within the 60-second window. Backend rejects with
  // 400 past the window; we hide the toast locally before then either
  // way (countdown drives the dismiss).
  const handleUndoPickup = async () => {
    if (!undoToast) return;
    setUndoSubmitting(true);
    try {
      await api.post(
        `/api/store/pickup-orders/${undoToast.orderId}/undo-pickup/`,
      );
      setToastMessage({
        kind: 'success',
        text: `Order ${undoToast.orderNumber} reverted to ready for pickup`,
      });
      setTimeout(() => setToastMessage(null), 4000);
      setUndoToast(null);
      fetchOrders();
      fetchLockers();
    } catch (err: any) {
      setToastMessage({
        kind: 'error',
        text: err.response?.data?.error || 'Undo failed',
      });
      setTimeout(() => setToastMessage(null), 4000);
      setUndoToast(null);
    } finally {
      setUndoSubmitting(false);
    }
  };

  // Tick once a second while the undo toast is visible so the
  // countdown display refreshes. Also auto-dismiss the toast when
  // the window expires.
  useEffect(() => {
    if (!undoToast) return;
    const interval = setInterval(() => {
      if (Date.now() >= undoToast.expiresAt) {
        setUndoToast(null);
      } else {
        setNowTick((n) => n + 1);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [undoToast]);

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      return `${Math.floor(hours / 24)}d ago`;
    } else if (hours > 0) {
      return `${hours}h ago`;
    } else if (mins > 0) {
      return `${mins}m ago`;
    }
    return 'Just now';
  };

  const availableLockers = lockers.filter(l => !l.is_occupied);

  if (loading) {
    return (
      <div className="pickup-orders-page">
        <div className="loading">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="pickup-orders-page">
      <div className="pickup-orders-header">
        <div className="header-title">
          <h1>Orders to Fill</h1>
          <p className="header-subtitle">Manage online orders waiting for pickup</p>
        </div>
        <button className="btn-refresh" onClick={() => { fetchOrders(); fetchLockers(); }}>
          Refresh
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats Cards */}
      {stats && (
        <div className="stats-row">
          <div className="stat-card stat-total">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Pending</div>
          </div>
          <div className="stat-card stat-needs-filling">
            <div className="stat-value">{stats.needs_filling}</div>
            <div className="stat-label">Needs Filling</div>
          </div>
          <div className="stat-card stat-locker">
            <div className="stat-value">{stats.locker_orders}</div>
            <div className="stat-label">Locker Pickup</div>
          </div>
          <div className="stat-card stat-instore">
            <div className="stat-value">{stats.instore_orders}</div>
            <div className="stat-label">In-Store Pickup</div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({orders.length})
        </button>
        <button
          className={`filter-tab ${filter === 'needs_filling' ? 'active' : ''}`}
          onClick={() => setFilter('needs_filling')}
        >
          Needs Filling ({orders.filter(o => o.status === 'paid').length})
        </button>
        <button
          className={`filter-tab ${filter === 'locker' ? 'active' : ''}`}
          onClick={() => setFilter('locker')}
        >
          Locker ({orders.filter(o => o.pickup_method === 'locker').length})
        </button>
        <button
          className={`filter-tab ${filter === 'in_store' ? 'active' : ''}`}
          onClick={() => setFilter('in_store')}
        >
          In-Store ({orders.filter(o => o.pickup_method === 'in_store').length})
        </button>
      </div>

      {/* Orders List */}
      <div className="orders-list">
        {filteredOrders.length === 0 ? (
          <div className="no-orders">
            <div className="no-orders-icon">📦</div>
            <div className="no-orders-text">No orders waiting</div>
          </div>
        ) : (
          filteredOrders.map(order => (
            <div
              key={order.id}
              className={`order-card ${order.status === 'paid' ? 'needs-filling' : 'ready'}`}
            >
              <div className="order-card-header">
                <div className="order-info">
                  <span className="pickup-icon">
                    {order.pickup_method === 'locker' ? '🔐' : '🏪'}
                  </span>
                  <span className="order-number">{order.order_number}</span>
                  {/* Phase B — camera badge when this order was
                      auto-completed via the Eufy unlock webhook.
                      Tooltip explains why the icon is there. */}
                  {order.completion_method === 'auto_via_webhook' && (
                    <span
                      title={
                        order.picked_up_at
                          ? `Auto-confirmed via locker unlock at ${new Date(order.picked_up_at).toLocaleString()}`
                          : 'Auto-confirmed via locker unlock'
                      }
                      style={{
                        fontSize: 14,
                        marginLeft: 4,
                        cursor: 'help',
                      }}
                    >
                      📷
                    </span>
                  )}
                  <span className="customer-name">{order.customer_name}</span>
                  <span className={`pickup-badge ${order.pickup_method}`}>
                    {order.pickup_method === 'locker' ? 'LOCKER PICKUP' : 'IN-STORE'}
                  </span>
                </div>
                <div className="order-time">{formatTime(order.created_at)}</div>
              </div>

              <div className="order-card-body">
                <div className="order-items">{order.items_summary}</div>
                <div className="order-total">${order.total.toFixed(2)}</div>
              </div>

              {/* Locker Info if assigned */}
              {order.locker_assignment && (
                <div className="locker-info">
                  <span className="locker-badge">
                    Locker #{order.locker_assignment.locker_number}
                  </span>
                  <span className="locker-passcode">
                    Code: <strong>{order.locker_assignment.passcode}</strong>
                  </span>
                </div>
              )}

              <div className="order-card-actions">
                {order.status === 'paid' && order.pickup_method === 'locker' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => handleAssignLocker(order)}
                  >
                    Fill Order & Assign Locker
                  </button>
                )}
                {order.status === 'paid' && order.pickup_method === 'in_store' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => handleMarkReady(order)}
                  >
                    Ready for Pickup
                  </button>
                )}
                {order.status === 'ready_for_pickup' && (
                  // Phase A — relabeled to "Manual Override" so staff
                  // treats this as the fallback path. Once the Eufy
                  // unlock webhook ships (commit #3), the normal flow
                  // becomes automatic; this button stays as the
                  // safety net for when the customer can't get the
                  // locker open or pays cash at the till.
                  <button
                    className="btn btn-success"
                    onClick={() => handleCompletePickup(order)}
                    title="Only use if the customer can't get the locker open, or for in-store cash pickups. The locker unlock will normally auto-confirm pickup."
                  >
                    Manual Override: Mark Picked Up
                  </button>
                )}
                <button className="btn btn-secondary">
                  View Details
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Assign Locker — shared component used by both this page and
          StoreOrders.tsx so the two staff workflows stay identical.
          Modal handles the full flow (locker pick, code, confirmation,
          submit, success); we just refresh on success. */}
      {showAssignModal && selectedOrder && (
        <AssignLockerModal
          order={{
            id: selectedOrder.id,
            order_number: selectedOrder.order_number,
            customer_name: selectedOrder.customer_name,
          }}
          lockers={lockers.map(l => ({
            id: l.id,
            number: l.number,
            name: l.name,
            is_occupied: l.is_occupied,
          }))}
          onSuccess={handleAssignSuccess}
          onClose={closeAssignModal}
        />
      )}

      {/* Phase A — Confirm Pickup Complete modal. Replaces the
          window.confirm dialog tonight's accidental click sailed
          through. Two-button explicit confirmation; Cancel is the
          default focus. */}
      {pickupConfirm && (
        <div
          className="modal-overlay"
          onClick={() => !pickupSubmitting && setPickupConfirm(null)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 480 }}
          >
            <div className="modal-header">
              <h2>Confirm Pickup Complete</h2>
              <button
                className="modal-close"
                onClick={() => setPickupConfirm(null)}
                disabled={pickupSubmitting}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 16, marginBottom: 12, color: '#374151' }}>
                Mark Order <strong>{pickupConfirm.order_number}</strong> as picked up?
              </p>
              <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 8 }}>
                This will:
              </p>
              <ul style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, paddingLeft: 20 }}>
                <li>Notify the customer their pickup is complete</li>
                {pickupConfirm.locker_assignment && (
                  <li>
                    Free up Locker #{pickupConfirm.locker_assignment.locker_number}{' '}
                    for the next order
                  </li>
                )}
                <li>Move the order to completed status</li>
              </ul>
              <p style={{
                fontSize: 13,
                color: '#92400E',
                background: '#FEF3C7',
                padding: 8,
                borderRadius: 4,
                marginTop: 12,
              }}>
                ⚠ This action can be undone within 60 seconds.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setPickupConfirm(null)}
                disabled={pickupSubmitting}
                autoFocus
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={confirmManualPickup}
                disabled={pickupSubmitting}
              >
                {pickupSubmitting ? 'Marking…' : 'Yes, Mark Picked Up'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase A — Undo toast. Appears after a successful manual
          pickup; auto-dismisses when the 60-second window expires.
          Backend rejects undo past the window with 400 — toast hides
          before then anyway. */}
      {undoToast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: '#065F46',
            color: 'white',
            padding: '14px 18px',
            borderRadius: 8,
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            zIndex: 9999,
            minWidth: 300,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              ✓ Order {undoToast.orderNumber} marked picked up
            </div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
              {Math.max(0, Math.ceil((undoToast.expiresAt - Date.now()) / 1000))}s
              to undo
            </div>
          </div>
          <button
            onClick={handleUndoPickup}
            disabled={undoSubmitting}
            style={{
              background: 'white',
              color: '#065F46',
              border: 'none',
              padding: '8px 14px',
              borderRadius: 6,
              fontWeight: 600,
              cursor: undoSubmitting ? 'wait' : 'pointer',
              fontSize: 13,
            }}
          >
            {undoSubmitting ? 'Undoing…' : 'Undo'}
          </button>
        </div>
      )}

      {/* General success/error toast (undo result, manual-pickup
          failure, etc.). Auto-clears after 4 seconds. */}
      {toastMessage && (
        <div
          style={{
            position: 'fixed',
            top: 24,
            right: 24,
            background: toastMessage.kind === 'success' ? '#ECFDF5' : '#FEF2F2',
            border: `1px solid ${toastMessage.kind === 'success' ? '#10B981' : '#FCA5A5'}`,
            color: toastMessage.kind === 'success' ? '#065F46' : '#991B1B',
            padding: '10px 14px',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            zIndex: 9999,
            maxWidth: 360,
          }}
        >
          {toastMessage.text}
        </div>
      )}
    </div>
  );
};

export default PickupOrders;
